import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { Anomaly } from '../models/anomaly.types'
import { uniqueDimensions, uniqueMetrics } from '../service/filterAnomalies'

import type { ApiError } from '@/shared/http'

export type AnomalyLoadStatus = 'idle' | 'loading' | 'ready' | 'error'

export const useAnomalyStore = defineStore('anomalies', () => {
  const records = ref<Anomaly[]>([])
  const status = ref<AnomalyLoadStatus>('idle')
  const error = ref<ApiError | null>(null)
  const loadedKey = ref<string | null>(null)

  const needsAttention = computed(() =>
    records.value.filter((record) => record.category === 'needs_attention'),
  )
  const informational = computed(() =>
    records.value.filter((record) => record.category === 'informational'),
  )
  const metricFields = computed(() => uniqueMetrics(records.value))
  const dimensions = computed(() => uniqueDimensions(records.value))
  const count = computed(() => records.value.length)

  function setLoading(key: string): void {
    loadedKey.value = key
    records.value = []
    status.value = 'loading'
    error.value = null
  }

  function setRecords(key: string, next: Anomaly[]): boolean {
    if (!isCurrent(key)) return false
    records.value = next
    status.value = 'ready'
    error.value = null
    return true
  }

  function setError(key: string, next: ApiError): boolean {
    if (!isCurrent(key)) return false
    error.value = next
    status.value = 'error'
    return true
  }

  function isCurrent(key: string): boolean {
    return loadedKey.value === key
  }

  function reset(): void {
    records.value = []
    status.value = 'idle'
    error.value = null
    loadedKey.value = null
  }

  return {
    records,
    status,
    error,
    loadedKey,
    needsAttention,
    informational,
    metricFields,
    dimensions,
    count,
    setLoading,
    setRecords,
    setError,
    isCurrent,
    reset,
  }
})

export type AnomalyStore = ReturnType<typeof useAnomalyStore>
